/**
 * MobileMenuUserItems — logged-in section of the mobile header menu Drawer
 * (BUG-CF-HEADER-RESP).
 *
 * Rendered ONLY when a user is authenticated, mirroring the desktop action row.
 * Lives in its own component so the cart/unread context hooks are called only
 * for logged-in users (same guarantee as MultiCartIcon) — AppShell stays usable
 * by guests without the MultiCartProvider in tests.
 */
import { NavLink, Divider, Indicator } from '@mantine/core';
import {
  IconHeart,
  IconMessage,
  IconShoppingCart,
  IconBuildingStore,
  IconWallet,
  IconListTree,
  IconShield,
  IconUser,
  IconLogout,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useMultiCartContext } from '@/features/cart';
import { useUnreadTotal } from '@/hooks/useConversations';

interface MobileMenuUserItemsProps {
  isAdmin: boolean;
  favoritesCount: number;
  /** Navigate to `to` and close the menu. */
  onNavigate: (to: string) => void;
  /** Close the menu (used before opening another drawer). */
  onCloseMenu: () => void;
  onOpenFavorites: () => void;
  onLogout: () => void;
}

function badge(count: number, color: string) {
  if (count <= 0) return undefined;
  return <Indicator color={color} label={count > 99 ? '99+' : count} size={16} position="middle-end" />;
}

export function MobileMenuUserItems({
  isAdmin,
  favoritesCount,
  onNavigate,
  onCloseMenu,
  onOpenFavorites,
  onLogout,
}: MobileMenuUserItemsProps) {
  const { t } = useTranslation('common');
  const { totalItems: cartCount, openDrawer: openCartDrawer } = useMultiCartContext();
  const { data: unreadTotal = 0 } = useUnreadTotal();

  return (
    <>
      <NavLink
        label={t('favorites.title')}
        leftSection={<IconHeart size={20} stroke={1.5} />}
        rightSection={badge(favoritesCount, 'red')}
        onClick={() => {
          onCloseMenu();
          onOpenFavorites();
        }}
        data-testid="menu-favorites"
      />
      <NavLink
        label={t('messages.title')}
        leftSection={<IconMessage size={20} stroke={1.5} />}
        rightSection={badge(unreadTotal, 'red')}
        onClick={() => onNavigate('/messages')}
        data-testid="menu-messages"
      />
      <NavLink
        label={t('my_carts')}
        leftSection={<IconShoppingCart size={20} stroke={1.5} />}
        rightSection={badge(cartCount, 'teal.8')}
        onClick={() => {
          onCloseMenu();
          openCartDrawer();
        }}
        data-testid="menu-carts"
      />

      <Divider my="xs" />

      <NavLink
        label={t('my_merchants')}
        leftSection={<IconBuildingStore size={20} stroke={1.5} />}
        onClick={() => onNavigate('/my-merchants')}
        data-testid="menu-my-merchants"
      />
      <NavLink
        label={t('finances')}
        leftSection={<IconWallet size={20} stroke={1.5} />}
        onClick={() => onNavigate('/finances')}
        data-testid="menu-finances"
      />

      {isAdmin && (
        <>
          <Divider my="xs" />
          <NavLink
            label={t('catalog')}
            leftSection={<IconListTree size={20} stroke={1.5} />}
            onClick={() => onNavigate('/admin/catalog')}
            data-testid="menu-catalog"
          />
          <NavLink
            label={t('admin')}
            leftSection={<IconShield size={20} stroke={1.5} />}
            onClick={() => onNavigate('/admin/users')}
            data-testid="menu-admin"
          />
        </>
      )}

      <Divider my="xs" />

      <NavLink
        label={t('profile')}
        leftSection={<IconUser size={20} stroke={1.5} />}
        onClick={() => onNavigate('/profile')}
        data-testid="menu-profile"
      />
      <NavLink
        label={t('logout')}
        c="red"
        leftSection={<IconLogout size={20} stroke={1.5} />}
        onClick={() => {
          onCloseMenu();
          onLogout();
        }}
        data-testid="menu-logout"
      />
    </>
  );
}
